import { Fragment, useRef, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, RefreshIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Pagination } from 'antd';
import { Loader } from '../../components/Loader';
import { fetchShippingPackageHistory } from '../../services/shippingPackageServices';

const ACTION_STYLES = {
    create: 'bg-green-100 text-green-800',
    update: 'bg-blue-100 text-blue-800',
    destroy: 'bg-red-100 text-red-800',
    restore: 'bg-yellow-100 text-yellow-800'
};

const HISTORY_FIELDS = [
    'name', 'length_cm', 'width_cm', 'height_cm', 'weight_kg', 'package_type',
    'volume_liters', 'cost', 'bar_code', 'volumetric_weight', 'store_id', 'active'
];

export const ShippingPackageHistoryModal = ({
    open,
    setOpen,
    packageData = null,
    warehouseId = null
}) => {
    const { i18n } = useTranslation();
    const cancelButtonRef = useRef(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        setPage(1);
    }, [packageData?.id]);

    const { data, isLoading, isFetching, refetch } = useQuery(
        ['shipping_package_history', packageData?.id, page],
        () => fetchShippingPackageHistory(packageData.id, { page, per_page: 10, warehouse_id: warehouseId }),
        {
            enabled: open && !!packageData?.id,
            keepPreviousData: true,
            refetchOnWindowFocus: false
        }
    );

    const histories = data?.shipping_package_histories || [];
    const meta = data?.meta || {};

    const getActionLabel = (action) => {
        return i18n.t(`shipping_packages.history.action_${action}`) || action;
    };

    const getFieldLabel = (field) => {
        return i18n.t(`shipping_packages.history.field_${field}`) || field;
    };

    const formatValue = (value) => {
        if (value === null || value === undefined) return '-';
        if (value === true) return i18n.t('shipping_packages.filters.active_only');
        if (value === false) return i18n.t('shipping_packages.filters.inactive_only');
        return String(value);
    };

    const getChangedFields = (entry) => {
        if (entry.action === 'create') {
            return HISTORY_FIELDS
                .filter(field => entry.after_change?.[field] != null)
                .map(field => ({
                    field,
                    oldValue: null,
                    newValue: entry.after_change[field]
                }));
        }

        if (entry.action === 'destroy' || entry.action === 'restore') {
            return [{
                field: 'active',
                oldValue: entry.before_change?.active,
                newValue: entry.after_change?.active
            }];
        }

        // update: show only fields that changed
        return HISTORY_FIELDS.filter(field => {
            const before = entry.before_change?.[field];
            const after = entry.after_change?.[field];
            return String(before) !== String(after);
        }).map(field => ({
            field,
            oldValue: entry.before_change?.[field],
            newValue: entry.after_change?.[field]
        }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString(i18n.language === 'pt' ? 'pt-BR' : 'es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleClose = () => {
        setOpen(false);
        setPage(1);
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                initialFocus={cancelButtonRef}
                onClose={handleClose}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
                            <div className="flex justify-between items-center mb-4">
                                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                    {i18n.t('shipping_packages.history.title')}
                                    {packageData?.name ? ` - ${packageData.name}` : ''}
                                </Dialog.Title>
                                <div className="flex items-center space-x-2">
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-blue-600 transition-colors"
                                        onClick={() => refetch()}
                                        disabled={isFetching}
                                        title={i18n.t('shipping_packages.history.refresh')}
                                    >
                                        <RefreshIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                                    </button>
                                    <button
                                        type="button"
                                        className="text-gray-400 hover:text-gray-500"
                                        onClick={handleClose}
                                        ref={cancelButtonRef}
                                    >
                                        <XIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="relative min-h-[200px]">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <Loader show={true} />
                                    </div>
                                ) : histories.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">{i18n.t('shipping_packages.history.no_history')}</p>
                                    </div>
                                ) : (
                                    <>
                                        {isFetching && <div className="absolute inset-0 bg-white bg-opacity-50 z-10" />}
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {i18n.t('shipping_packages.history.action')}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {i18n.t('shipping_packages.history.changes')}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {i18n.t('shipping_packages.history.user')}
                                                        </th>
                                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                            {i18n.t('shipping_packages.history.date')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {histories.map((entry) => (
                                                        <tr key={entry.id}>
                                                            <td className="px-4 py-3 whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}>
                                                                    {getActionLabel(entry.action)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="space-y-1 text-sm">
                                                                    {getChangedFields(entry).map(({ field, oldValue, newValue }) => (
                                                                        <div key={field} className="flex items-center gap-1 flex-wrap">
                                                                            <span className="font-medium text-gray-700">{getFieldLabel(field)}:</span>
                                                                            {oldValue != null && (
                                                                                <>
                                                                                    <span className="text-red-500 line-through">{formatValue(oldValue)}</span>
                                                                                    <span className="text-gray-400">&rarr;</span>
                                                                                </>
                                                                            )}
                                                                            <span className="text-green-600">{formatValue(newValue)}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {entry.created_by?.email || '-'}
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                                {formatDate(entry.created_at)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {meta.total_pages > 1 && (
                                            <div className="flex justify-end mt-4">
                                                <Pagination
                                                    size="small"
                                                    current={meta.current_page}
                                                    total={meta.total_count}
                                                    pageSize={10}
                                                    onChange={(p) => setPage(p)}
                                                    showSizeChanger={false}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
